import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { useInRouterContext, useLocation } from 'react-router-dom'
import InlineIcon from '../InlineIcon/InlineIcon.jsx'
import './ErrorBoundary.scss'
import log from '../../lib/logging/log.js'
import { posthogCapture } from '../../lib/logging/posthog.js'
import browserSniffer from '../../lib/helpers/browserSniffer.js'
import { isChunkLoadError } from '../../lib/lazyWithRetry.js'

// Note: Error boundaries must remain as class components as there's no functional equivalent
// for componentDidCatch/getDerivedStateFromError in hooks yet
class ErrorBoundaryInner extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      reference: '',
      isSafari: false,
      isChunkError: false,
    }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, isChunkError: isChunkLoadError(error) }
  }

  // The crash handler only LOGS. It must never wipe cookies/localStorage or
  // redirect: it used to, and a crash without a kindeToken (mid-callback,
  // or on the login page itself) destroyed the just-persisted refresh token
  // and could loop the login forever.
  componentDidCatch(error, info) {
    const reference = Math.random().toString(36).substring(6).toUpperCase()
    const browser = browserSniffer.getBrowserInfo()
    const isSafari = browser.name === 'Safari'
    const isChunkError = isChunkLoadError(error)

    const userInfo = this._getUserInfo()

    this.setState({
      hasError: true,
      reference,
      isSafari,
      isChunkError,
    })

    const logPayload = {
      reference,
      errorData: error,
      errorInfo: info,
      userInfo,
    }

    posthogCapture('error_boundary_crash', {
      reference,
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
      userInfo,
      chunkLoad: isChunkError,
    })

    // A chunk that failed to download is a transport problem, not a code fault.
    // It must not page anyone on FATAL, and it must never reach the session
    // teardown below — a dropped request is not a reason to log someone out.
    if (isChunkError) {
      log.warn(`CHUNK_LOAD_FAILED: ${error.message}`, logPayload)
      return
    }

    if (isSafari) {
      log.warn(`FATAL: ${error.message}`, logPayload)
    } else {
      log.error(`FATAL: ${error.message}`, logPayload)
    }
  }

  // A crash must not brick every subsequent SPA navigation: when the route
  // changes, give the new route a clean try instead of keeping the sticky
  // error UI until a manual reload.
  componentDidUpdate(prevProps) {
    if (
      this.state.hasError &&
      prevProps.locationKey !== this.props.locationKey
    ) {
      this.setState({ hasError: false, reference: '', isSafari: false })
    }
  }

  _getUserInfo = () => {
    try {
      return JSON.parse(localStorage.getItem('userInfo')) || {}
    } catch {
      return {
        first_name: 'unknown',
        last_name: 'unknown',
        company: 'unknown',
        role: 'unknown',
      }
    }
  }

  handleRestart = () => {
    window.location.reload()
  }

  _getMessage = () => {
    const { isChunkError, isSafari } = this.state

    // A failed download outranks the Safari notice: the browser is not the
    // problem, the connection is.
    if (isChunkError) {
      return `We couldn't load part of the application. Check your connection and reload.`
    }
    if (isSafari) {
      return `To ensure the best app experience, please consider using Chrome, Firefox, or Opera.`
    }
    return `An error has occurred. Our engineers have been notified and will be correcting the error. Please restart the application.`
  }

  render() {
    const { hasError, isChunkError, reference } = this.state

    if (hasError) {
      const message = this._getMessage()

      return (
        <div className='error-boundary'>
          <h2>{isChunkError ? 'Connection Problem' : 'Application Error'}</h2>
          <p>{message}</p>
          <hr />
          <p>
            <strong>Reference Number:</strong> {reference}
          </p>
          <hr />
          <button
            type='button'
            className='error-boundary__restart'
            onClick={this.handleRestart}
          >
            <InlineIcon name='refresh' />
            Restart Application
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

ErrorBoundaryInner.propTypes = {
  children: PropTypes.node.isRequired,
  locationKey: PropTypes.string,
}

function RoutedErrorBoundary({ children }) {
  const location = useLocation()
  return (
    <ErrorBoundaryInner locationKey={location.pathname}>
      {children}
    </ErrorBoundaryInner>
  )
}

RoutedErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
}

/**
 * Uses the location-aware variant when a Router is above us, so the boundary
 * resets on navigation; falls back to a plain boundary otherwise (tests,
 * fragments rendered outside the router).
 */
function ErrorBoundary({ children }) {
  return useInRouterContext() ? (
    <RoutedErrorBoundary>{children}</RoutedErrorBoundary>
  ) : (
    <ErrorBoundaryInner>{children}</ErrorBoundaryInner>
  )
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
}

export default ErrorBoundary
